import { ENEMIES } from '../../data/enemies';
import { SIM_HZ } from '../../data/tuning';
import { createAabb, createCircle, type Aabb, type Circle } from '../collision';

/**
 * EN-31 Fumigadora (GDD §8.3, issue #116). Pure and deterministic: the plane flies with the player at `worldSpeed`
 * from the tick it was first active, computed from the tick count (no accumulated float), and sprays a puff every
 * `sprayEverySeconds` while it is on screen. A puff is fixed in the world and hurts while its age is below
 * `puffSeconds` (240 ticks): at the tick 239 it still hurts, at 240 it is gone. The plane itself never gets destroyed.
 */
export const EN31 = ENEMIES['EN-31'];

export const SPRAY_TICKS = Math.round(EN31.sprayEverySeconds * SIM_HZ);
export const PUFF_TICKS = Math.round(EN31.puffSeconds * SIM_HZ);

export interface Puff {
  x: number;
  y: number;
  /** Ticks since it was sprayed. */
  age: number;
}

export interface CropDusterState {
  /** Ticks since it was first active (it keeps counting after it leaves the screen). */
  ticks: number;
  /** Ticks since the last puff. */
  sinceSpray: number;
  puffs: Puff[];
}

export const createCropDuster = (): CropDusterState => ({ ticks: 0, sinceSpray: SPRAY_TICKS, puffs: [] });

/** x of the plane `ticks` ticks after it was first active, from where it started. */
export const dusterX = (startX: number, ticks: number): number => startX + (EN31.worldSpeed * ticks) / SIM_HZ;

/**
 * One tick after the plane has been placed: the existing puffs age and the expired ones go; if the plane is on screen
 * and it is time, a new puff is sprayed where the plane is. Returns true when a puff was sprayed.
 */
export function stepCropDuster(state: CropDusterState, at: { x: number; y: number }, onScreen: boolean): boolean {
  state.ticks += 1;
  for (const puff of state.puffs) puff.age += 1;
  state.puffs = state.puffs.filter((puff) => puff.age < PUFF_TICKS);
  state.sinceSpray += 1;
  if (!onScreen || state.sinceSpray < SPRAY_TICKS) return false;
  state.sinceSpray = 0;
  state.puffs.push({ x: at.x, y: at.y, age: 0 });
  return true;
}

/** The body of the plane: a box of 1.4 x 0.5 u (like EN-03). */
export const cropDusterBody = (x: number, y: number): Aabb => createAabb(x, y, EN31.hitboxWidth, EN31.hitboxHeight);

export const puffCircle = (puff: Puff): Circle => createCircle(puff.x, puff.y, EN31.puffRadius);
